<template>
    <div class="boa-date-picker-panel">
        <div class="flex-row-with-gap">
            <period-picker
                v-if="!props.selectionLocked"
                hide-timer
                :model-value="localDatePeriod"
                :period-override-mode="localPeriodOverrideMode"
                @update:model-value="updateDatePeriod"
            />
            <info-icon-with-tooltip
                tooltip-text="Specific period overrides will not be saved to your settings so refreshing will clear this override."
            />
        </div>
        <select-button
            class="boa-period-options"
            :model-value="localPeriodOverrideMode"
            :options="periodToUsePageOverrideOptions"
            option-label="name"
            option-value="id"
            :allow-empty="false"
            :disabled="props.selectionLocked"
            @update:model-value="updatePeriodOverrideMode"
        >
            <template #option="{ option }">
                <span class="period-option-label">{{ option.name }}</span>
                <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
            </template>
        </select-button>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import PeriodPicker from '@/components/powerMarkets/balancing/PeriodPicker.vue';
import SelectButton from 'primevue/selectbutton';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { periodToUsePageOverrideOptions } from '@/constants/powerMarkets/periodPickerOverrides.ts';

const props = defineProps<{
    modelValue: IDatePeriod;
    periodOverrideMode?: PeriodTypeOverridesWithInherited;
    selectionLocked?: boolean;
}>();

const localDatePeriod = ref<IDatePeriod>(props.modelValue);
const localPeriodOverrideMode = ref<PeriodTypeOverridesWithInherited>(props.periodOverrideMode ?? 'inherited');

function updateDatePeriod (datePeriod: IDatePeriod): void {
    localDatePeriod.value = datePeriod;
}

function updatePeriodOverrideMode (periodOverrideMode: PeriodTypeOverridesWithInherited): void {
    localPeriodOverrideMode.value = periodOverrideMode;
}

function getPayload (): { datePeriod: IDatePeriod; periodOverrideMode: PeriodTypeOverridesWithInherited } {
    return {
        datePeriod: localDatePeriod.value,
        periodOverrideMode: localPeriodOverrideMode.value,
    };
}

defineExpose({
    getPayload,
});
</script>

<style lang="scss" scoped>
.boa-date-picker-panel {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
}

.period-option-label {
    white-space: nowrap;
}

</style>
